import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Instagram, Linkedin, Facebook } from "lucide-react";
import { NaaviLogo, ShapeFlower } from "./brand/Shapes";
import { HEADER_MENUS } from "../data/site";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [email, setEmail] = useState("");
  const [contactMsg, setContactMsg] = useState({ name: "", email: "", message: "" });

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list. Look out for our next letter from Naavi.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email.");
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!contactMsg.name || !contactMsg.email || !contactMsg.message) {
      toast.error("Please fill out all fields.");
      return;
    }
    try {
      await axios.post(`${API}/contact`, contactMsg);
      toast.success("Message sent. We'll reply within one working day.");
      setContactMsg({ name: "", email: "", message: "" });
    } catch {
      toast.error("Please try again in a moment.");
    }
  };

  return (
    <>
      <section
        id="contact"
        className="relative py-14 md:py-28 bg-[#E3EDF9] overflow-hidden"
        data-testid="contact-section"
      >
        <ShapeFlower
          size={160}
          color="#C6D947"
          className="absolute -bottom-10 -right-10 opacity-60 hidden md:block"
        />

        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <span className="text-xs font-bold tracking-[0.22em] uppercase text-[#648DE5] mb-5 block">
                Come say hello
              </span>
              <h2 className="font-display text-[#0F1B47] text-[32px] md:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-semibold">
                Questions? Coffee?
                <br />
                <span className="italic font-normal">We'd love to meet</span>.
              </h2>
              <div className="mt-10 space-y-5">
                <ContactRow
                  icon={<MapPin size={18} />}
                  label="Campus"
                  value="Naavi Early Years, 99, Inner Circle, Dodsworth Layout, Whitefield, Bangalore 560066"
                />
                <ContactRow
                  icon={<Phone size={18} />}
                  label="Admissions line"
                  value="+91 97405 31855"
                />
                <ContactRow
                  icon={<Mail size={18} />}
                  label="Email"
                  value="info@naaviearlyyears.edu.in"
                />
              </div>
              <div className="mt-8 flex items-center gap-4">
                <SocialLink icon={<Instagram size={18} />} label="Instagram" href="#" />
                <SocialLink icon={<Linkedin size={18} />} label="LinkedIn" href="#" />
                <SocialLink icon={<Facebook size={18} />} label="Facebook" href="#" />
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7 bg-[#FAFAF5] rounded-[32px] p-8 md:p-10 border border-[#0F1B47]/6"
              data-testid="contact-form"
            >
              <h3 className="font-display text-2xl text-[#0F1B47] font-medium mb-6">
                Send us a quick message
              </h3>
              <form onSubmit={sendMessage} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    value={contactMsg.name}
                    onChange={(e) => setContactMsg({ ...contactMsg, name: e.target.value })}
                    placeholder="Your name"
                    className={inputCls}
                    data-testid="contact-name"
                  />
                  <input
                    type="email"
                    value={contactMsg.email}
                    onChange={(e) => setContactMsg({ ...contactMsg, email: e.target.value })}
                    placeholder="Your email"
                    className={inputCls}
                    data-testid="contact-email"
                  />
                </div>
                <textarea
                  rows={5}
                  value={contactMsg.message}
                  onChange={(e) => setContactMsg({ ...contactMsg, message: e.target.value })}
                  placeholder="Your message"
                  className={`${inputCls} resize-none`}
                  data-testid="contact-message"
                />
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-8 py-4 font-semibold text-[15px] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1a2b6b] hover:shadow-xl"
                  data-testid="contact-submit"
                >
                  Send message
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1B47] text-[#FDFDF9] pt-20 pb-10 relative overflow-hidden" data-testid="site-footer">
        <div
          className="absolute -top-20 -right-20 w-72 h-72 rounded-full"
          style={{ background: "#3AB0D6", opacity: 0.15 }}
        />
        <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 flex-wrap">
                <NaaviLogo height={44} variant="light" />
                <img
                  decoding="async"
                  loading="lazy"
                  src="/images/cambridge-logo-light.png"
                  alt="Cambridge Early Years Centre"
                  style={{ height: 52, width: "auto" }}
                  data-testid="footer-cambridge-logo"
                />
              </div>
              <p className="mt-6 text-[#FDFDF9]/70 text-base leading-relaxed font-light max-w-md">
                An international Cambridge-affiliated early years school for children
                1.5 — 6. Based in Bengaluru. Inspired everywhere.
              </p>

              <form onSubmit={subscribe} className="mt-8 flex gap-2 max-w-md" data-testid="newsletter-form">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Join our letters from Naavi"
                  className="flex-1 bg-[#FDFDF9]/8 border border-[#FDFDF9]/20 rounded-full px-5 py-3 text-sm text-[#FDFDF9] placeholder:text-[#FDFDF9]/50 focus:outline-none focus:border-[#C6D947]"
                  data-testid="newsletter-email"
                />
                <button
                  type="submit"
                  className="bg-[#C6D947] text-[#0F1B47] rounded-full px-6 py-3 font-semibold text-sm transition-all hover:-translate-y-0.5"
                  data-testid="newsletter-submit"
                >
                  Subscribe
                </button>
              </form>
            </div>

            <div className="lg:col-span-3">
              <h4 className="text-xs font-bold tracking-[0.2em] uppercase text-[#C6D947] mb-5">
                Explore
              </h4>
              <ul className="space-y-3 text-[#FDFDF9]/80">
                {HEADER_MENUS.map((m) => (
                  <li key={m.href}>
                    <Link to={m.href} className="hover:text-[#C6D947] transition-colors">
                      {m.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-4">
              <h4 className="text-xs font-bold tracking-[0.2em] uppercase text-[#C6D947] mb-5">
                Visit us
              </h4>
              <p className="text-[#FDFDF9]/80 text-sm leading-relaxed">
                99, Inner Circle, Dodsworth Layout
                <br />
                Whitefield, Bangalore 560066
                <br />
                Mon — Fri · 8:30 AM — 3:30 PM
              </p>
              <p className="text-[#FDFDF9]/80 text-sm mt-4">+91 97405 31855</p>
              <p className="text-[#FDFDF9]/80 text-sm">info@naaviearlyyears.edu.in</p>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-[#FDFDF9]/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-[#FDFDF9]/50">
            <div>© {new Date().getFullYear()} Naavi Early Years. All rights reserved.</div>
            <div className="flex gap-6">
              <a href="/legal/naavi-privacy-policy.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-[#FDFDF9]" data-testid="footer-privacy">Privacy Policy</a>
              <a href="/legal/naavi-terms-and-conditions.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-[#FDFDF9]" data-testid="footer-terms">Terms and Conditions</a>
              <a href="/legal/naavi-return-refund-policy.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-[#FDFDF9]" data-testid="footer-refund">Return and Refund Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

const inputCls =
  "w-full bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-2xl px-5 py-3.5 text-[15px] text-[#0F1B47] placeholder:text-[#718096] font-medium focus:outline-none focus:border-[#648DE5] focus:ring-4 focus:ring-[#648DE5]/10 transition-all";

function ContactRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-full bg-[#E3EDF9] flex items-center justify-center text-[#0F1B47] flex-shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-xs font-bold tracking-[0.18em] uppercase text-[#648DE5] mb-1">
          {label}
        </div>
        <div className="text-[#0F1B47] text-[15px] font-medium">{value}</div>
      </div>
    </div>
  );
}

function SocialLink({ icon, label, href }) {
  return (
    <a
      href={href}
      aria-label={label}
      className="w-11 h-11 rounded-full border border-[#0F1B47]/15 flex items-center justify-center text-[#0F1B47] transition-all hover:bg-[#0F1B47] hover:text-[#FDFDF9] hover:-translate-y-0.5"
      data-testid={`social-${label.toLowerCase()}`}
    >
      {icon}
    </a>
  );
}
